import { useMemo } from "react";
import { FiscalYearSelector } from "../components/fiscal/FiscalYearSelector.js";
import { KpiCard } from "../components/fiscal/KpiCard.js";
import { FiscalCard, FiscalShell, LoadingSkeleton } from "../components/fiscal/Shell.js";
import { useFiscalSelection } from "../hooks/useFiscalSelection.js";
import { computeDelais, findLastBalanceEntry } from "../lib/fiscal-derived.js";
import { formatDays, formatMoney, toNumber } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";

export default function FiscalBilan() {
  const { companyId, fiscalYearId, setCompanyId, setFiscalYearId } = useFiscalSelection();
  const t = useT();

  const calcQuery = trpc.fiscalCalculations.getByFiscalYear.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null },
  );
  const fyQuery = trpc.fiscalYears.getById.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null },
  );

  const calc = calcQuery.data;
  const entries = fyQuery.data?.entries ?? [];
  const lastEntry = useMemo(() => findLastBalanceEntry(entries), [entries]);

  const delais = useMemo(
    () =>
      calc
        ? computeDelais({
            caHt: calc.caHt,
            achatsConsommes: calc.achatsConsommes,
            nbJoursExercice: calc.nbJoursExercice,
            lastBalanceEntry: lastEntry,
          })
        : null,
    [calc, lastEntry],
  );

  const toolbar = (
    <FiscalYearSelector
      companyId={companyId}
      fiscalYearId={fiscalYearId}
      onChangeCompany={setCompanyId}
      onChangeFiscalYear={(fy) => setFiscalYearId(fy?.id ?? null)}
    />
  );

  if (companyId === null || fiscalYearId === null) {
    return (
      <FiscalShell subtitle={t("fiscalBilan.subtitle")} toolbar={toolbar}>
        <FiscalCard>
          <p className="text-sm text-neutral-400">{t("fiscalBilan.selectCompanyFy")}</p>
        </FiscalCard>
      </FiscalShell>
    );
  }

  if (calcQuery.isLoading || fyQuery.isLoading) {
    return (
      <FiscalShell subtitle={t("fiscalBilan.subtitle")} toolbar={toolbar}>
        <FiscalCard>
          <LoadingSkeleton rows={8} />
        </FiscalCard>
      </FiscalShell>
    );
  }

  if (!calc) {
    return (
      <FiscalShell subtitle={t("fiscalBilan.subtitle")} toolbar={toolbar}>
        <FiscalCard title={t("fiscalBilan.noKpisTitle")}>
          <p className="text-sm text-neutral-400">{t("fiscalBilan.runRecompute")}</p>
        </FiscalCard>
      </FiscalShell>
    );
  }

  const noBilan = lastEntry === null;
  const actifImmo = toNumber(lastEntry?.immobilisationsNettes ?? null) ?? 0;
  const actifCreances = toNumber(lastEntry?.creancesClients ?? null) ?? 0;
  const actifAutresC = toNumber(lastEntry?.autresCreances ?? null) ?? 0;
  const actifTreso = toNumber(lastEntry?.tresorerie ?? null) ?? 0;
  const totalActif = actifImmo + actifCreances + actifAutresC + actifTreso;

  const passifCapitaux = toNumber(lastEntry?.capitauxPropres ?? null) ?? 0;
  const passifDettesFin = toNumber(lastEntry?.dettesFinancieres ?? null) ?? 0;
  const passifDettesFourn = toNumber(lastEntry?.dettesFournisseurs ?? null) ?? 0;
  const passifAutresD = toNumber(lastEntry?.autresDettes ?? null) ?? 0;
  const totalPassif = passifCapitaux + passifDettesFin + passifDettesFourn + passifAutresD;

  return (
    <FiscalShell subtitle={t("fiscalBilan.subtitle")} toolbar={toolbar}>
      {noBilan && (
        <FiscalCard title={t("fiscalBilan.bilanNotFilledTitle")}>
          <p className="text-sm text-neutral-400">
            {t("fiscalBilan.bilanNotFilledPre")} <strong>{t("fiscalBilan.entriesLabel")}</strong>{" "}
            {t("fiscalBilan.bilanNotFilledSuf")}
          </p>
        </FiscalCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label={t("fiscalBilan.fondsRoulement")}
          value={formatMoney(calc.fr)}
          tooltip={t("fiscalBilan.tooltipFr")}
        />
        <KpiCard
          label={t("fiscalBilan.bfr")}
          value={formatMoney(calc.bfr)}
          tooltip={t("fiscalBilan.tooltipBfr")}
        />
        <KpiCard
          label={t("fiscalBilan.tresorerieNette")}
          value={formatMoney(calc.tresorerieNette)}
          tone={(toNumber(calc.tresorerieNette) ?? 0) < 0 ? "red" : "neutral"}
          tooltip={t("fiscalBilan.tooltipTresorerieNette")}
        />
        <KpiCard
          label={t("fiscalBilan.ecartBilanTreso")}
          value={formatMoney(calc.tresorerieDiff)}
          secondaryLabel={t("fiscalBilan.tresorerieEntry")}
          secondaryValue={formatMoney(calc.tresorerieEntry)}
          tone={Math.abs(toNumber(calc.tresorerieDiff) ?? 0) > 0.01 ? "amber" : "neutral"}
          tooltip={t("fiscalBilan.tooltipEcart")}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label={t("fiscalBilan.dso")}
          value={formatDays(delais?.dso ?? null)}
          tone={(delais?.dso ?? 0) > 60 ? "amber" : "neutral"}
          tooltip={t("fiscalBilan.tooltipDso")}
        />
        <KpiCard
          label={t("fiscalBilan.dpo")}
          value={formatDays(delais?.dpo ?? null)}
          tooltip={t("fiscalBilan.tooltipDpo")}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FiscalCard
          title={t("fiscalBilan.actif")}
          subtitle={
            lastEntry
              ? `${t("fiscalBilan.bilanAsOf")} ${String(lastEntry.month).padStart(2, "0")}/${lastEntry.year}`
              : "—"
          }
        >
          <table className="w-full text-sm">
            <tbody className="divide-y divide-neutral-900">
              <RowItem
                label={t("fiscalBilan.immobilisationsNettes")}
                value={lastEntry?.immobilisationsNettes ?? null}
              />
              <RowItem
                label={t("fiscalBilan.creancesClients")}
                value={lastEntry?.creancesClients ?? null}
              />
              <RowItem
                label={t("fiscalBilan.autresCreances")}
                value={lastEntry?.autresCreances ?? null}
              />
              <RowItem label={t("fiscalBilan.tresorerie")} value={lastEntry?.tresorerie ?? null} />
            </tbody>
            <tfoot className="border-t border-neutral-800">
              <tr className="bg-lime/5">
                <td className="px-3 py-2 font-semibold">{t("fiscalBilan.totalActif")}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">
                  {formatMoney(totalActif)}
                </td>
              </tr>
            </tfoot>
          </table>
        </FiscalCard>

        <FiscalCard
          title={t("fiscalBilan.passif")}
          subtitle={
            lastEntry
              ? `${t("fiscalBilan.bilanAsOf")} ${String(lastEntry.month).padStart(2, "0")}/${lastEntry.year}`
              : "—"
          }
        >
          <table className="w-full text-sm">
            <tbody className="divide-y divide-neutral-900">
              <RowItem
                label={t("fiscalBilan.capitauxPropres")}
                value={lastEntry?.capitauxPropres ?? null}
              />
              <RowItem
                label={t("fiscalBilan.dettesFinancieres")}
                value={lastEntry?.dettesFinancieres ?? null}
              />
              <RowItem
                label={t("fiscalBilan.dettesFournisseurs")}
                value={lastEntry?.dettesFournisseurs ?? null}
              />
              <RowItem
                label={t("fiscalBilan.autresDettes")}
                value={lastEntry?.autresDettes ?? null}
              />
            </tbody>
            <tfoot className="border-t border-neutral-800">
              <tr className="bg-lime/5">
                <td className="px-3 py-2 font-semibold">{t("fiscalBilan.totalPassif")}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">
                  {formatMoney(totalPassif)}
                </td>
              </tr>
            </tfoot>
          </table>
        </FiscalCard>
      </div>

      {Math.abs(totalActif - totalPassif) > 0.01 && lastEntry && (
        <FiscalCard>
          <p className="text-sm text-amber-300">
            ⚠ {t("fiscalBilan.actif")} ({formatMoney(totalActif)}) {t("fiscalBilan.mismatchMid")} (
            {formatMoney(totalPassif)}) {t("fiscalBilan.mismatchSuf")}{" "}
            {formatMoney(totalActif - totalPassif)}.
          </p>
        </FiscalCard>
      )}
    </FiscalShell>
  );
}

function RowItem({ label, value }: { label: string; value: string | number | null }) {
  const t = useT();
  return (
    <tr>
      <td className="px-3 py-1.5 text-neutral-300">{label}</td>
      <td className="px-3 py-1.5 text-right font-mono tabular-nums">
        {value === null ? (
          <span className="text-neutral-700">{t("fiscalBilan.emptyValue")}</span>
        ) : (
          formatMoney(value)
        )}
      </td>
    </tr>
  );
}
